// A messenger pigeon, modelled facing +X: a plump grey body on two stub legs,
// a small head with a short beak, folded dark wings along the flanks, a fanned
// tail and a tiny brass message canister strapped to one leg. The looping
// "idle" bobs the head and sways the body ever so slightly — a bird at rest on
// the loft board, waiting for its dispatch.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const FEATHER = "#8f939b";   // body grey
const WING = "#5f636b";      // wing / tail dark
const BEAK = "#d9a13b";      // hazard-yellow beak reads as horn
const LEG = "#c98a7a";       // pink legs
const BRASS = "#b08d3a";     // message canister
const EYE = "#1c140e";

function pigeonParts(): Part[] {
  const body = (p: Part): Part => ({ ...p, name: "body" });
  const head = (p: Part): Part => ({ ...p, name: "head" });
  return [
    // stub legs (static, keep the feet planted while the body sways)
    { geometry: new THREE.CylinderGeometry(0.0035, 0.003, 0.018, 4), color: LEG, position: [0.006, 0.009, 0.011] },
    { geometry: new THREE.CylinderGeometry(0.0035, 0.003, 0.018, 4), color: LEG, position: [0.006, 0.009, -0.011] },
    // brass message canister strapped to the right leg
    { geometry: new THREE.CylinderGeometry(0.004, 0.004, 0.01, 6), color: BRASS, position: [0.006, 0.011, 0.015] },
    // plump body, breast pushed forward-down
    body({ geometry: new THREE.IcosahedronGeometry(0.026, 0), color: FEATHER, position: [0, 0.036, 0], scale: [1.35, 1.0, 0.95], rotation: [0, 0, 0.25] }),
    body({ geometry: new THREE.IcosahedronGeometry(0.018, 0), color: FEATHER, position: [0.02, 0.03, 0], scale: [1.0, 0.95, 0.9] }),
    // folded wings tucked along the flanks, tips toward the tail
    body({ geometry: new THREE.BoxGeometry(0.038, 0.011, 0.007), color: WING, position: [-0.002, 0.039, 0.018], rotation: [0, 0.1, 0.2] }),
    body({ geometry: new THREE.BoxGeometry(0.038, 0.011, 0.007), color: WING, position: [-0.002, 0.039, -0.018], rotation: [0, -0.1, 0.2] }),
    // fanned tail sweeping back and slightly up out of the rump
    body({ geometry: new THREE.BoxGeometry(0.028, 0.006, 0.018), color: WING, position: [-0.028, 0.042, 0], rotation: [0, 0, 0.35] }),
    // small head on a short neck, with beak and eyes
    head({ geometry: new THREE.IcosahedronGeometry(0.012, 0), color: FEATHER, position: [0.028, 0.062, 0] }),
    head({ geometry: new THREE.ConeGeometry(0.004, 0.012, 4), color: BEAK, position: [0.04, 0.061, 0], rotation: [0, 0, -Math.PI / 2] }),
    head({ geometry: new THREE.IcosahedronGeometry(0.0022, 0), color: EYE, position: [0.031, 0.066, 0.009] }),
    head({ geometry: new THREE.IcosahedronGeometry(0.0022, 0), color: EYE, position: [0.031, 0.066, -0.009] }),
  ];
}

export const messengerPigeon = defineModel({
  id: "messenger-pigeon",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Messenger Pigeon",
  tags: ["creature", "animal", "bird", "wwi", "signals", "animated"],
  summary: "A plump grey messenger pigeon with a brass dispatch canister on one leg.",
  description:
    "A standing carrier pigeon of the signals service: grey body, folded dark " +
    "wings, fanned tail and a tiny brass message canister strapped to its right " +
    "leg. The looping idle bobs its head and rocks the body gently at rest.",
  author: "Fable 5",
  footprint: 0.06,
  parts: pigeonParts,
  animations: [
    {
      id: "idle",
      label: "Idle",
      duration: 2.0,
      loop: true,
      tracks: [
        {
          // quick pigeon head-bob, twice per cycle
          target: "head",
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.5, value: [0.004, -0.005, 0], ease: "sine" },
            { t: 1.0, value: [0, 0, 0], ease: "sine" },
            { t: 1.5, value: [0.004, -0.005, 0], ease: "sine" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          // slight body sway about the feet
          target: "body",
          pivot: [0, 0.018, 0],
          rotation: [
            { t: 0, value: [0.04, 0, 0] },
            { t: 1.0, value: [-0.04, 0, 0], ease: "sine" },
            { t: 2.0, value: [0.04, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
